import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription, firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  ImpersonationApprovalPopupService,
  type ImpersonationApprovalPopupItem,
} from '../../../core/services/notification/impersonation-approval-popup.service';
import { NotificationService } from '../../../core/services/notification/notification.service';
import { ImpersonationSessionService } from '../../../core/services/impersonation/impersonation-session.service';
import { ToastService } from '../../../core/services/toast/toast.service';

@Component({
  selector: 'app-impersonation-approval-popup',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './impersonation-approval-popup.component.html',
  styleUrl: './impersonation-approval-popup.component.css',
})
export class ImpersonationApprovalPopupComponent implements OnInit, OnDestroy {
  readonly popupService = inject(ImpersonationApprovalPopupService);
  private readonly notificationService = inject(NotificationService);
  private readonly impersonationSession = inject(ImpersonationSessionService);
  private readonly toast = inject(ToastService);
  private readonly http = inject(HttpClient);

  readonly denyingRequestId = signal<string | null>(null);
  private readonly impersonationActive = signal(this.impersonationSession.isActive());
  private sessionSub: Subscription | null = null;

  /** Hide approval UI entirely while an impersonation session is active. */
  readonly visiblePopups = computed(() =>
    this.impersonationActive() ? [] : this.popupService.popups(),
  );

  ngOnInit(): void {
    this.sessionSub = this.impersonationSession.state$.subscribe((state) => {
      this.impersonationActive.set(state.isActive);
      if (state.isActive) {
        this.popupService.dismissAll();
      }
    });
  }

  ngOnDestroy(): void {
    this.sessionSub?.unsubscribe();
  }

  trackById(_index: number, item: ImpersonationApprovalPopupItem): string {
    return item.id;
  }

  dismiss(id: string, ev: Event): void {
    ev.preventDefault();
    ev.stopPropagation();
    this.popupService.dismiss(id);
  }

  remainingLabel(item: ImpersonationApprovalPopupItem): string {
    const ms = Date.parse(item.expiresAt) - Date.now();
    if (!Number.isFinite(ms) || ms <= 0) {
      return 'Expired';
    }
    const minutes = Math.ceil(ms / 60000);
    return minutes <= 1 ? 'Expires in under 1 min' : `Expires in ${minutes} min`;
  }

  async onDeny(item: ImpersonationApprovalPopupItem, ev: Event): Promise<void> {
    ev.preventDefault();
    ev.stopPropagation();

    if (this.denyingRequestId()) {
      return;
    }

    if (this.popupService.isExpired(item)) {
      this.toast.error('Request expired', {
        message: 'This impersonation request is no longer valid.',
      });
      this.popupService.dismiss(item.id);
      return;
    }

    this.denyingRequestId.set(item.requestId);
    try {
      await firstValueFrom(
        this.http.post(`${environment.apiUrl}/impersonation/deny-approval`, {
          requestId: item.requestId,
        }),
      );
      if (item.notificationId) {
        await this.notificationService.markAsRead(item.notificationId);
      }
      this.popupService.dismiss(item.id);
      this.toast.success('Impersonation request denied');
    } catch {
      this.toast.error('Could not deny request', {
        message: 'Please try again.',
      });
    } finally {
      this.denyingRequestId.set(null);
    }
  }
}
